import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { drivelabels_v2 } from '@googleapis/drivelabels';
import {
  doApiCheck,
  getAllDriveLabelsForDriveSearchQuery,
  UserKeys,
} from '../../api/users-api';
import {
  ChoiceBadgeInfo,
  getAllLabelBadges,
  getChoiceBadgeInfo,
} from '../google-color';
import { isFeatureEnabled } from '../../utils/feature-flags';
import {
  isAbleToSeeUIElement,
  DriveRoles,
} from '../../utils/roles/client-roles';
import { useUserSettings } from '../../utils/use-user-settings';
import { useTheme } from '@mui/material/styles';

export interface LabelBadgeInfo {
  readonly selectedBadge?: ChoiceBadgeInfo;
  readonly allBadges: ChoiceBadgeInfo[];
}

export function useDriveLabelBadges(driveRoles: string[]) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { features } = useUserSettings();

  const driveLabelsFeatureEnabled = isFeatureEnabled(
    'driveLabelsRead',
    features
  );
  const canViewDriveLabels = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CanViewDriveLabels.value
  );

  const {
    data: driveLabelsAccessGranted,
    isLoading: checkingDriveLabelsAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'driveLabels'],
    queryFn: () => doApiCheck('driveLabels', null),
    staleTime: 0,
    enabled: driveLabelsFeatureEnabled && canViewDriveLabels,
  });

  const { data: driveLabelsForColors } = useQuery({
    ...getAllDriveLabelsForDriveSearchQuery(),
    enabled:
      driveLabelsFeatureEnabled &&
      canViewDriveLabels &&
      !!driveLabelsAccessGranted,
  });

  const labelById = useMemo(() => {
    const map = new Map<
      string,
      drivelabels_v2.Schema$GoogleAppsDriveLabelsV2Label
    >();
    (driveLabelsForColors?.labels ?? []).forEach((entry) => {
      if (entry.hasBadge && entry.label.id) {
        map.set(entry.label.id, entry.label);
      }
    });
    return map;
  }, [driveLabelsForColors]);

  const allBadgesCache = useMemo(
    () => new Map<string, ChoiceBadgeInfo[]>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- labelById/isDarkMode are invalidation triggers for the cache, not values read by the factory
    [labelById, isDarkMode]
  );

  const getLabelBadgeInfo = useCallback(
    (labelId: string, choiceIds: string[]): LabelBadgeInfo => {
      const label = labelById.get(labelId);
      if (!label) {
        return { selectedBadge: undefined, allBadges: [] };
      }

      let selectedBadge: ChoiceBadgeInfo | undefined;
      for (const choiceId of choiceIds) {
        selectedBadge = getChoiceBadgeInfo(label, choiceId, isDarkMode);
        if (selectedBadge) {
          break;
        }
      }

      let allBadges = allBadgesCache.get(labelId);
      if (!allBadges) {
        allBadges = getAllLabelBadges(label, isDarkMode);
        allBadgesCache.set(labelId, allBadges);
      }

      return { selectedBadge, allBadges };
    },
    [labelById, isDarkMode, allBadgesCache]
  );

  return {
    driveLabelsFeatureEnabled,
    canViewDriveLabels,
    driveLabelsAccessGranted: !!driveLabelsAccessGranted,
    checkingDriveLabelsAccess,
    getLabelBadgeInfo,
  };
}
